import { Button as ChakraButton, Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { Check, Code2, Copy } from "lucide-react";
import { useMemo, useState } from "react";
import { useUser } from "../../hooks/use-user";
import useApiRequest from "../../lib/hooks/useRequest";
import { Button } from "../../ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../ui/components/ui/dialog";
import { Input } from "../../ui/components/ui/input";
import { Textarea } from "../../ui/components/ui/textarea";
import { buildPatientRequestEmbedSnippet, buildPatientRequestFormUrl } from "./patient-request-embed-snippet";

/** Reicht fuer die Formularpruefung — die eigentliche Pruefung macht der Server. */
const EMAIL_MUSTER = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type WebmasterVersandStatus = "idle" | "sending" | "success" | "error";

/**
 * Der Weg, auf dem ein Betrieb das Anfrageformular auf seine Website bringt
 * (Issue KAN-233, KAN-242, KAN-254).
 *
 * Es gibt zwei Wege dorthin, und sie schliessen sich gegenseitig aus: Entweder pflegt der
 * Betrieb seine Website selbst und braucht den Code, oder eine Agentur pflegt sie und er
 * braucht nur deren Adresse. Darum steht jeder Weg auf einem eigenen Reiter statt beides
 * untereinander auf einer Flaeche — vorher stand der Versand unter dem Code-Feld und damit
 * dort, wo die meisten schon aufgehoert haben zu lesen (KAN-254).
 *
 * Die Vorschau steht neben den Reitern und gehoert keinem von beiden: Sie zeigt, was der
 * Besucher am Ende sieht, und das ist auf beiden Wegen dasselbe.
 *
 * Die Eingaben zum Versand liegen bewusst hier und nicht im Reiter selbst: Der geschlossene
 * Reiter wird abgeraeumt, eine halb getippte Adresse waere sonst beim Zurueckwechseln weg.
 *
 * Dieselbe Komponente steht in der Kopfzeile der Liste und im Leerzustand — zwei
 * Einstiege, ein Weg. Wer kein Organisations-Administrator ist, sieht nichts davon:
 * Der Schnipsel ist eine Entscheidung ueber den Aussenauftritt, nicht ueber eine Anfrage.
 */
export const PatientRequestEmbedCode = () => {
  const { user } = useUser();
  const { apiRequest } = useApiRequest();
  const [isCopied, setIsCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  // Der zweite Weg neben dem Kopieren (Issue KAN-242): Viele Kunden pflegen ihre Website
  // nicht selbst, sondern lassen eine Agentur das einbinden.
  const [webmasterMail, setWebmasterMail] = useState("");
  const [webmasterMailFehler, setWebmasterMailFehler] = useState<string | null>(null);
  const [webmasterVersandStatus, setWebmasterVersandStatus] = useState<WebmasterVersandStatus>("idle");

  const organizationId = user?.organizationId;

  const snippet = useMemo(
    () => (organizationId ? buildPatientRequestEmbedSnippet(organizationId, window.location.origin) : ""),
    [organizationId]
  );
  const formUrl = useMemo(
    () => (organizationId ? buildPatientRequestFormUrl(organizationId, window.location.origin) : ""),
    [organizationId]
  );

  // Ohne Administrator-Recht oder ohne bekannte Organisations-Kennung gibt es den
  // Abschnitt nicht — ein halber Schnipsel waere schlimmer als keiner.
  if (!user?.permissionOrganizationAdmin || !organizationId) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopyFailed(false);
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 1500);
    } catch (err) {
      // Aelteres Browser-Umfeld oder blockierte Zwischenablage: Dann bleibt der Weg
      // von Hand, und der Kunde braucht dafuer einen Hinweis statt einer stillen Pleite.
      console.error("Failed to copy text: ", err);
      setCopyFailed(true);
    }
  };

  const handleWebmasterMailChange = (wert: string) => {
    setWebmasterMail(wert);
    // Eine neue Eingabe loescht die alte Rueckmeldung — sonst stuende nach dem Korrigieren
    // noch der Fehler von vorhin da, als waere nichts passiert.
    setWebmasterMailFehler(null);
    setWebmasterVersandStatus("idle");
  };

  const handleSendToWebmaster = async () => {
    const adresse = webmasterMail.trim();
    if (!EMAIL_MUSTER.test(adresse)) {
      setWebmasterMailFehler("Bitte geben Sie eine gültige E-Mail-Adresse ein.");
      setWebmasterVersandStatus("idle");
      return;
    }

    setWebmasterMailFehler(null);
    setWebmasterVersandStatus("sending");

    const antwort = await apiRequest<unknown>("patient-requests/embed-code/send", "POST", { body: { mail: adresse } });

    // Ein gescheiterter Versand sieht nie wie ein erfolgreicher aus. Das Fehlerfeld allein
    // reicht dafuer nicht: Wirft fetch selbst (offline, DNS, CORS), meldet useRequest zwar
    // Status 500, laesst das Fehlerfeld aber leer. Darum zaehlt nur ein Erfolgs-Status.
    const erfolgreich = !antwort.error && antwort.status >= 200 && antwort.status < 300;
    setWebmasterVersandStatus(erfolgreich ? "success" : "error");
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        {/* Derselbe Knopf wie ueberall sonst in kandidat.io: colorScheme "primary" aus dem
            Thema statt einer fest eingetragenen Farbe. Aendert sich das Thema, zieht er mit. */}
        <ChakraButton colorScheme="primary" variant="solid" height="40px" className="flex gap-2 text-white">
          <Code2 className="h-4 w-4" />
          Formular einbinden
        </ChakraButton>
      </DialogTrigger>

      {/* Ohne das setzt der Dialog den Fokus beim Oeffnen ins erste Feld, und das ist hier
          das Code-Feld — es steht dann blau umrandet da, als haette man hineingeklickt. */}
      <DialogContent className="max-w-4xl" onOpenAutoFocus={event => event.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Anfrageformular auf Ihrer Website einbinden</DialogTitle>
          <DialogDescription>Jede abgesendete Anfrage landet in dieser Liste.</DialogDescription>
        </DialogHeader>

        {/* Die Vorschau steht neben den Reitern, nicht in einem von ihnen: Sie zeigt, was der
            Besucher spaeter sieht, und das gilt auf beiden Wegen gleich. Wer die Adresse der
            Agentur eintippt, soll dabei vor Augen haben, was er dort einbauen laesst. */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Dieselbe Machart wie die Reiter in den Einstellungen und beim Job-Formular. */}
          <Tabs isLazy>
            {/* Neben der Vorschau ist die Spalte halb so breit wie der Dialog. In der
                Standardgroesse brechen beide Beschriftungen dort auf zwei Zeilen um und der
                Reiter sieht aus wie ein Absatz — darum kleiner und ohne Umbruch. */}
            <TabList>
              <Tab sx={{ color: "#7C8080", fontSize: "sm", px: 3, whiteSpace: "nowrap" }}>Selbst einbinden</Tab>
              <Tab sx={{ color: "#7C8080", fontSize: "sm", px: 3, whiteSpace: "nowrap" }}>
                An den Webbetreuer schicken
              </Tab>
            </TabList>

            <TabPanels pt={4}>
              <TabPanel p={0}>
                <h4 className="mb-2 text-sm font-bold">So gehen Sie vor</h4>
                <ol className="mb-4 list-decimal space-y-1.5 pl-5 text-sm text-gray-600">
                  <li>Code kopieren.</li>
                  <li>Die Seite Ihrer Website öffnen und den Code dort einfügen.</li>
                  <li>Speichern und veröffentlichen.</li>
                </ol>
                <p className="mb-4 text-sm text-gray-500">Der Code enthält bereits die Kennung Ihres Betriebs.</p>

                <label className="text-sm font-bold" htmlFor="patient-request-embed-snippet">
                  Ihr Einbindungs-Code
                </label>
                <div className="relative mt-1">
                  <Textarea
                    id="patient-request-embed-snippet"
                    value={snippet}
                    readOnly
                    className="resize-none pr-12 font-mono text-xs"
                    // Hoch genug, dass der ganze Schnipsel ohne Scrollen dasteht. Wer nur
                    // einen Teil sieht, fragt sich, ob er alles kopiert hat.
                    rows={10}
                  />
                  <Button
                    onClick={handleCopy}
                    className="absolute right-2 top-2 h-8 w-8 p-2"
                    variant="ghost"
                    size="icon"
                    aria-label="Einbindungs-Code kopieren"
                  >
                    {/* Der gruene Haken ist die ganze Rueckmeldung — ein Satz daneben sagt dasselbe noch einmal. */}
                    {isCopied ? <Check className="h-6 w-6 text-green-600" /> : <Copy className="h-6 w-6" />}
                  </Button>
                </div>
                {copyFailed && (
                  <p className="mt-2 text-sm text-red-600">
                    Kopieren hat nicht geklappt. Markieren Sie den Code bitte von Hand und kopieren Sie ihn mit Strg+C.
                  </p>
                )}
              </TabPanel>

              {/* Der zweite Weg neben dem Kopieren (Issue KAN-242): Pflegt eine Agentur oder ein
                  Bekannter die Website, erspart das den Umweg ueber Kopieren und Weiterleiten. */}
              <TabPanel p={0}>
                <h4 className="mb-1 text-sm font-bold">Kümmert sich jemand anderes um Ihre Website?</h4>
                <p className="mb-4 text-sm text-gray-500">
                  Dann schicken wir den Code direkt dorthin. Die Nachricht enthält den fertigen Code für Ihren Betrieb
                  und eine kurze Anleitung zum Einbauen. Sie müssen nichts kopieren und nichts weiterleiten.
                </p>

                <label className="text-sm font-bold" htmlFor="patient-request-embed-webmaster-mail">
                  E-Mail-Adresse des Webbetreuers
                </label>
                <div className="mt-1 flex flex-col items-start gap-2">
                  <Input
                    id="patient-request-embed-webmaster-mail"
                    type="email"
                    placeholder="agentur@beispiel.de"
                    value={webmasterMail}
                    onChange={event => handleWebmasterMailChange(event.target.value)}
                    disabled={webmasterVersandStatus === "sending"}
                  />
                  <Button onClick={handleSendToWebmaster} disabled={webmasterVersandStatus === "sending"} variant="outline">
                    An meinen Webbetreuer schicken
                  </Button>
                </div>
                {webmasterMailFehler && <p className="mt-2 text-sm text-red-600">{webmasterMailFehler}</p>}
                {webmasterVersandStatus === "success" && (
                  <p className="mt-2 text-sm text-green-700">Der Einbindungs-Code wurde verschickt.</p>
                )}
                {webmasterVersandStatus === "error" && (
                  <p className="mt-2 text-sm text-red-600">
                    Der Versand hat nicht geklappt. Bitte prüfen Sie die Adresse und versuchen Sie es noch einmal.
                  </p>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>

          <div>
            <h4 className="mb-2 text-sm font-bold">Vorschau</h4>
            <p className="mb-2 text-sm text-gray-500">So sieht das Formular auf Ihrer Website aus.</p>
            {/* Die Vorschau laedt genau die Adresse, die auch im Schnipsel steht — was hier
                steht, steht spaeter beim Kunden. */}
            <iframe
              title="Vorschau des Anfrageformulars"
              src={formUrl}
              className="h-[460px] w-full rounded-md border border-gray-200 bg-white"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
